/**
 * @fileoverview Component for the Publisher Setup summary table, with rows for GBP and Facebook.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */

import {ApplePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/appleconfiguration';
import {LinkedAccount} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/linkedaccount';
import {ViewStepButton} from '/src/com/yext/listingslaunchstorm/js/enterprise/summary/viewstepbutton';
import {EnterpriseConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/enterpriseconfiguration';
import {TaskIds} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/taskids';
import {Table} from '/ui/components/table/table';
import {Tooltip} from '/ui/components/tooltip/tooltip';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/summary/summary.module.scss';
import AppleLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/apple-logo.svg';
import FacebookLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/facebook-logo.svg';
import GoogleBusinessProfileLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/google-business-profile-logo.svg';
import YelpLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/yelp-logo.svg';

const COLUMN_IDS = {
  PUBLISHER: 'publisher',
  LINKED_ACCOUNT: 'linkedAccount',
  ACTION: 'action',
};

const GOOGLE_PUBLISHER_ID = 715;
const FACEBOOK_PUBLISHER_ID = 559;
const APPLE_PUBLISHER_ID = ApplePublisherId;
const YEXT_APPLE_ACCOUNT_NAME = yext.msg('Yext\'s Apple Account');
const APPLE_DELEGATION_TOOLTIP_MESSAGE = yext.msg('We will launch using the Yext Apple account since you opted out of adding your own.');
const YELP_ENTERPRISE_PUBLISHER_ID = 588;
const YELP_KNOWLEDGE_PUBLISHER_ID = 792;

/**
 * @param {{
 *   enterpriseConfiguration: EnterpriseConfiguration,
 *   onViewStep: function(string): void,
 *   publisherIds: Array<number>,
 * }} props
 * @returns {React.ReactNode}
 */
export function PublisherSetupTable({
  enterpriseConfiguration,
  onViewStep,
  publisherIds,
}) {
  const dataRows = getRows(enterpriseConfiguration, onViewStep, publisherIds);

  return (
    dataRows.length > 0 && <Table
      label={yext.msg('Summary of completed Publisher Setup steps')}
      columns={getColumns()}
      dataRows={dataRows}
    />
  );
}

/** @returns {!Array<?>} !Array<Table.Types.Column */
function getColumns() {
  return [
    {
      id: COLUMN_IDS.PUBLISHER,
      label: yext.msg('Publisher'),
    },
    {
      id: COLUMN_IDS.LINKED_ACCOUNT,
      label: yext.msg('Linked Account'),
    },
    {
      id: COLUMN_IDS.ACTION,
      label: undefined,
    },
  ];
}

/**
 * @param {EnterpriseConfiguration} enterpriseConfiguration
 * @param {function(string): void} onViewStep
 * @param {Array<number>} publisherIds
 * @returns {!Array<?>} !Array<Table.Types.Row>
 */
function getRows(enterpriseConfiguration, onViewStep, publisherIds) {
  const rows = [];
  if (publisherIds.includes(GOOGLE_PUBLISHER_ID)) {
    rows.push({
      id: 'gbpsetup',
      [COLUMN_IDS.PUBLISHER]: (
        <>
          <GoogleBusinessProfileLogoSvg className={styles.icon} />
          {yext.msg('Google Business Profile')}
        </>
      ),
      [COLUMN_IDS.LINKED_ACCOUNT]: getContent(
        enterpriseConfiguration.gbpConfiguration?.linkedAccount,
        enterpriseConfiguration.gbpConfiguration?.skipSetup,
      ),
      [COLUMN_IDS.ACTION]: (
        <ViewStepButton onClick={() => onViewStep(TaskIds.GBP_LINKED_ACCOUNT)} />
      ),
    });
  }
  if (publisherIds.includes(FACEBOOK_PUBLISHER_ID)) {
    rows.push({
      id: 'facebooksetup',
      [COLUMN_IDS.PUBLISHER]: (
        <>
          <FacebookLogoSvg className={styles.icon} />
          {yext.msg('Facebook')}
        </>
      ),
      [COLUMN_IDS.LINKED_ACCOUNT]: getContent(
        enterpriseConfiguration.facebookConfiguration?.linkedAccount,
        enterpriseConfiguration.facebookConfiguration?.skipSetup,
      ),
      [COLUMN_IDS.ACTION]: (
        <ViewStepButton onClick={() => onViewStep(TaskIds.FACEBOOK_LINKED_ACCOUNT)} />
      ),
    });
  }
  if (publisherIds.includes(APPLE_PUBLISHER_ID)) {
    rows.push({
      id: 'applesetup',
      [COLUMN_IDS.PUBLISHER]: (
        <>
          <AppleLogoSvg className={styles.icon} />
          {yext.msg('Apple')}
        </>
      ),
      [COLUMN_IDS.LINKED_ACCOUNT]:
        enterpriseConfiguration.appleConfiguration?.delegateAppleAccount
          ? getContent(
            enterpriseConfiguration.appleConfiguration?.linkedAccount,
            enterpriseConfiguration.appleConfiguration?.skipSetup,
            YEXT_APPLE_ACCOUNT_NAME,
            APPLE_DELEGATION_TOOLTIP_MESSAGE,
          )
          : getContent(
            enterpriseConfiguration.appleConfiguration?.linkedAccount,
            enterpriseConfiguration.appleConfiguration?.skipSetup,
          ),
      [COLUMN_IDS.ACTION]: (
        <ViewStepButton onClick={() => onViewStep(TaskIds.APPLE_LINKED_ACCOUNT)} />
      ),
    });
  }
  if (publisherIds.includes(YELP_ENTERPRISE_PUBLISHER_ID) || publisherIds.includes(YELP_KNOWLEDGE_PUBLISHER_ID)) {
    rows.push({
      id: 'yelpsetup',
      [COLUMN_IDS.PUBLISHER]: (
        <>
          <YelpLogoSvg className={styles.icon}/>
          {yext.msg('Yelp')}
        </>
      ),
      [COLUMN_IDS.LINKED_ACCOUNT]: getContent(
        enterpriseConfiguration.yelpConfiguration?.linkedAccount,
        enterpriseConfiguration.yelpConfiguration?.skipSetup,
      ),
      [COLUMN_IDS.ACTION]: (
        <ViewStepButton onClick={() => onViewStep(TaskIds.YELP_LINKED_ACCOUNT)} />
      ),
    });
  }
  return rows;
}

/**
 * @param {LinkedAccount | undefined} linkedAccount
 * @param {boolean=} skipSetup
 * @param {string=} customLinkedAccountName
 * @param {string=} customTooltipMessage
 * @returns {React.ReactNode}
 */
function getContent(linkedAccount, skipSetup=false, customLinkedAccountName, customTooltipMessage) {
  if (linkedAccount && !skipSetup) {
    const email = (linkedAccount.emailAddress !== undefined && linkedAccount.emailAddress.length > 0)
      ? ` (${linkedAccount.emailAddress})`
      : '';
    return `${linkedAccount.name}` + email;
  } else {
    return (
      <Tooltip
        unclearText={customLinkedAccountName ?? yext.msg('No Linked Account')}
        tooltipText={customTooltipMessage ?? yext.msg('You skipped linking this account. You can return to the step to link an account.')}
        direction="top"
      />
    );
  }
}
